// App-wide DOM-level behaviors that apply to every instance of a native
// control or MUI component, regardless of which React component renders it.
// Wired up once from index.js so no individual component needs to opt in.

// Globally restrict date input year to 4 digits.
// Native date values are always "YYYY-MM-DD"; over-typing produces e.g. "55555-02-05".
export function enableFourDigitYearDateInputs() {
  document.addEventListener(
    "input",
    (e) => {
      const el = e.target;
      if (el.tagName !== "INPUT" || el.type !== "date" || !el.value) return;
      const parts = el.value.split("-");
      if (parts[0] && parts[0].length > 4) {
        parts[0] = parts[0].slice(0, 4);
        el.value = parts.join("-");
        el.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },
    true // capture phase — runs before React handlers
  );
}

// Globally make every MUI Dialog draggable by its title bar. No per-dialog
// changes needed — MUI applies the same classes (.MuiDialogTitle-root /
// .MuiDialog-paper) to every Dialog instance regardless of which component
// renders it, so one listener here covers all of them.
export function enableDraggableDialogs() {
  const cursorHint = document.createElement("style");
  cursorHint.textContent =
    ".MuiDialogTitle-root { cursor: grab; } .MuiDialogTitle-root:active { cursor: grabbing; }";
  document.head.appendChild(cursorHint);

  let dragState = null;

  const onPointerDown = (e) => {
    const title = e.target.closest(".MuiDialogTitle-root");
    if (!title) return;
    // Don't start a drag from an interactive control inside the title bar (e.g. a close icon).
    if (e.target.closest("button, a, input, textarea, select, svg")) return;

    const paper = title.closest(".MuiDialog-paper");
    if (!paper || paper.classList.contains("MuiDialog-paperFullScreen")) return;

    const point = e.touches ? e.touches[0] : e;

    // First drag on this dialog: freeze it at its current rendered position with
    // position:fixed so it can be moved without MUI's flex layout re-centering it.
    if (!paper.dataset.dragFixed) {
      const rect = paper.getBoundingClientRect();
      paper.style.position = "fixed";
      paper.style.margin = "0";
      paper.style.left = `${rect.left}px`;
      paper.style.top = `${rect.top}px`;
      paper.dataset.dragFixed = "1";
    }

    dragState = {
      paper,
      startX: point.clientX,
      startY: point.clientY,
      startLeft: parseFloat(paper.style.left),
      startTop: parseFloat(paper.style.top),
    };
    document.body.style.userSelect = "none";
  };

  const onPointerMove = (e) => {
    if (!dragState) return;
    const point = e.touches ? e.touches[0] : e;
    dragState.paper.style.left = `${dragState.startLeft + (point.clientX - dragState.startX)}px`;
    dragState.paper.style.top = `${dragState.startTop + (point.clientY - dragState.startY)}px`;
  };

  const onPointerUp = () => {
    dragState = null;
    document.body.style.userSelect = "";
  };

  document.addEventListener("mousedown", onPointerDown);
  document.addEventListener("mousemove", onPointerMove);
  document.addEventListener("mouseup", onPointerUp);
  document.addEventListener("touchstart", onPointerDown, { passive: true });
  document.addEventListener("touchmove", onPointerMove, { passive: true });
  document.addEventListener("touchend", onPointerUp);
}

// Text-like inputs whose value is safe to trim on focus. Deliberately excludes
// password — trimming would silently change a credential — along with the
// numeric, date and choice types, whose values never carry padding.
const TRIMMABLE_INPUT_TYPES = ["text", "search", "email", "tel", "url"];

// Globally strip surrounding whitespace from a text field when the user focuses
// it, so the caret lands at the end of the real value instead of out in the
// padding (ENB-556).
//
// Many MLMS columns are fixed-width CHAR, so the API hands values back padded —
// "SMITH               " rather than "SMITH". Clicking into a field bound to one
// dropped the caret into the middle of the box and made the user clear the blanks
// before they could type. One listener here covers every text field in the app,
// so no individual component has to trim while binding. It only touches fields
// that actually have padding, so focusing a clean field does nothing at all.
export function enableTrimmedTextInputs() {
  document.addEventListener("focusin", (e) => {
    const el = e.target;
    const isTextArea = el.tagName === "TEXTAREA";
    if (!isTextArea) {
      if (el.tagName !== "INPUT") return;
      if (!TRIMMABLE_INPUT_TYPES.includes(el.type)) return;
    }
    if (el.readOnly || el.disabled) return;

    const trimmed = el.value.trim();
    if (trimmed === el.value) return;

    // Assigning `el.value` would run the setter React installs on the node, which
    // updates React's change tracker and so makes React treat the edit as a
    // no-op — the component would keep the padded value and re-render it right
    // back. Writing through the prototype setter leaves that tracker stale, so
    // the input event below does reach the component's onChange and state stays
    // in sync with what is on screen.
    const prototype = isTextArea
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(prototype, "value").set.call(el, trimmed);
    el.dispatchEvent(new Event("input", { bubbles: true }));

    try {
      el.setSelectionRange(trimmed.length, trimmed.length);
    } catch {
      // A few types (email) don't support the selection API. The browser clamps
      // the caret to the shortened value on its own, which is what we wanted.
    }
  });
}
